"use client";
import ColorIndicatorAtom from "@components/atoms/ColorIndicatorAtom";
import styles from "./ColorComparatorCard.module.scss";
import assetLoader from "@utils/internalImageLoder";
import ImageAtom from "@components/atoms/Image";
import { IDistinctColor } from "@interfaces/modelColorDetails";
import { useState } from "react";
import { toKebabCase } from "@utils/stringFormatters";
import ImageResponsive from "@components/atoms/ImageResponsive";
import ColorSelectionModal from "@components/organisms/ColorSelectionModal";
import {
  ColorComparatorCardData,
  defaultColorComparatorData,
} from "./colorComparatorData";
import { transformToColorSelectionData } from "./colorDataTransformer";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";

interface ColorComparatorCardProps {
  data?: ColorComparatorCardData;
  onColorChange?: (color: IDistinctColor) => void;
  colors?: IDistinctColor[];
  side?: "left" | "right";
  brand?: string;
  model?: string;
  value?: string;
  level?: "variant" | "modal";
  title?: string;
  pageType?: string;
  carAvailability?: string;
  cityName?: string;
}

export const ColorComparatorCard = ({
  data,
  onColorChange,
  colors: legacyColors,
  side: legacySide,
  brand: legacyBrand,
  model: legacyModel,
  value: legacyValue,
  level: legacyLevel,
  title: legacyTitle,
  pageType,
  carAvailability,
  cityName,
}: ColorComparatorCardProps) => {
  const componentData = data || {
    ...defaultColorComparatorData,
    colors: legacyColors || [],
    side: legacySide || "left",
    brand: legacyBrand || "",
    model: legacyModel || "",
    value: legacyValue || "",
    level: legacyLevel || "modal",
    title: legacyTitle,
  };

  const {
    colors,
    side,
    brand,
    model,
    value,
    level = "modal",
    title,
    modalOptions,
  } = componentData;

  const [selectedColor, setSelectedColor] = useState(
    value ? colors.findIndex((color) => color.name === value) : 0
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleColorSelect = (color: IDistinctColor) => {
    const colorIndex = colors.findIndex((c) => c.id === color.id);
    trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_choose_a_colour",
        section: "Compare",
        car_availability: carAvailability,
        city_name: cityName,
        vehicle_make: legacyBrand,
        vehicle_model: legacyModel,
      },
    });
    setSelectedColor(colorIndex);
    onColorChange?.(color);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const currentColor = colors.at(selectedColor);

  // Transform data for the existing ColorSelectionModal
  const modalData = transformToColorSelectionData(
    colors,
    level === "variant" ? `${brand} ${model}` : title || "Select Colour",
    modalOptions?.confirmButtonText || "Confirm Selection",
    undefined, // stockStatus
    modalOptions?.includePricingAndDelivery || false // Use option from data or default to false
  );

  const handleModalColorSelect = (colorVariant: any) => {
    // Find the original IDistinctColor from the selected ColorVariant
    const originalColor = colors.find(
      (c) => c.id.toString() === colorVariant.id
    );
    if (originalColor) {
      handleColorSelect(originalColor);
    }
  };

  return (
    <div className={styles["card"]}>
      {/* Color Selection Trigger - Opens Modal */}
      <div
        className={styles["card__dropdown__trigger"]}
        onClick={openModal}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openModal();
          }
        }}
      >
        <ColorIndicatorAtom color={currentColor?.hexCodes.at(0)} />
        <p>{currentColor?.name}</p>
        <ImageAtom
          src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
          alt="Chevron Down"
          width={20}
          height={20}
        />
      </div>

      <ImageResponsive
        filePath={`${toKebabCase(brand)}/${toKebabCase(model)}/${toKebabCase(
          currentColor?.name || ""
        )}`}
        imageTag={`Transparent`}
        width={600}
        height={300}
        alt="Colour Sample"
        className={styles["card__img"]}
      />

      {/* Use the existing ColorSelectionModal */}
      <ColorSelectionModal
        isOpen={isModalOpen}
        onClose={closeModal}
        data={modalData}
        onGetQuote={handleModalColorSelect}
        hidePricingAndDelivery={modalOptions?.hidePricingAndDelivery ?? true} // Use option from data or default to true
      />
    </div>
  );
};
